import {
    Badge,
    CellContentPosition,
    Icon,
    LicensePlate,
    Stack,
    TableCellData,
    TableColumnData,
    TableRowData,
    Box,
    LinkButton,
    Loader,
    Typography,
} from "@tm/components"
import { useCountryCodeToLicensePlate, useWorkTask } from "@tm/context-distribution"
import { useLocalization } from "@tm/localization"
import { connectComponent } from "@tm/morpheus"
import { encodeUniqueId, renderRoute, uniqueId } from "@tm/utils"
import { useEffect } from "react"
import { useHistory, useParams } from "react-router"
import { getWorkTaskLabellingFromListItem } from "../../helpers"
import { Actions, IActions } from "./business"
import { RecentListState, WorkTaskListItem } from "./business/model"
import { VehicleImage, LatestActivitiesWidgetCard, LatestActivitiesTable } from "./StyledComponents"

type Props = {
    state: RecentListState
    actions: IActions
    customerListRoute: string
}

function RecentList(props: Props) {
    const history = useHistory()
    const matchParams = useParams<{ workTaskId?: string }>()
    const context = useWorkTask()
    const localization = useLocalization()
    const { translateText, formatDate: renderDate } = localization
    const { plateCode } = useCountryCodeToLicensePlate(context?.workTask?.vehicle?.countryCode)

    useEffect(() => {
        props.actions.load()
    }, [])

    const renderDateAndPlateCell = (workTask: WorkTaskListItem) => {
        const { createdDate, modifiedDate, vehiclePlateId } = workTask
        const date = modifiedDate || createdDate
        return (
            <Stack gap={0.5}>
                <Box fontFamily="Roboto Condensed">
                    <Typography variant="body3">{renderDate(date, "T")}</Typography>
                    <br />
                    <Typography variant="body3">{renderDate(date, "d")}</Typography>
                </Box>
                {vehiclePlateId && <LicensePlate size="large" countryCode={plateCode} value={vehiclePlateId} />}
            </Stack>
        )
    }

    const renderTotalNumbersCell = (workTask: WorkTaskListItem) => {
        const { partPositionsTotal, workPositionsTotal } = workTask
        return (
            <Box>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Icon name="article" />
                    <Badge color="primary" size="small" badgeContent={partPositionsTotal || "0"} />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Icon name="repairtimes" />
                    <Badge color="primary" size="small" badgeContent={workPositionsTotal || "0"} />
                </Stack>
            </Box>
        )
    }

    const renderVehicleImageCell = (workTask: WorkTaskListItem) => {
        return <VehicleImage src={workTask.vehicleThumbnail} noFallback />
    }

    const renderCustomerAndVehicleCell = (workTask: WorkTaskListItem) => {
        const labelling = getWorkTaskLabellingFromListItem(workTask, localization)
        return (
            <Typography variant="body2">
                {labelling.title}
                <br />
                {!!labelling.info && labelling.info}
            </Typography>
        )
    }

    function getColumns() {
        const columns: TableColumnData[] = []

        columns.push(
            { header: "", gridSize: "1fr" }, // License plate and date
            { header: "", gridSize: "70px" }, // Total Numbers
            { header: "", gridSize: "80px" }, // Vehicle image
            { header: "", gridSize: "2fr", alignContent: CellContentPosition.left } // Customer and vehicle
        )

        return columns
    }

    function getCells(workTask: WorkTaskListItem) {
        const data: TableCellData[] = []

        data.push(
            { displayValue: renderDateAndPlateCell(workTask), id: "1" },
            { displayValue: renderTotalNumbersCell(workTask), id: "2" },
            { displayValue: renderVehicleImageCell(workTask), id: "3" },
            { displayValue: renderCustomerAndVehicleCell(workTask), id: "4" }
        )

        return data
    }

    const displayData: TableRowData[] = props.state.workTasks.map((workTask) => ({
        cells: getCells(workTask),
        id: `${workTask.workTaskId}`,
        customRow: false,
        active: false,
    }))

    const renderWidgetHeaderAction = () => {
        const url = renderRoute(props.customerListRoute, { ...matchParams, workTaskId: matchParams.workTaskId || encodeUniqueId(uniqueId()) })
        return <LinkButton to={url}>{translateText(175)}</LinkButton>
    }

    const handleOnRowClick = (workTaskId: string) => {
        const url = `/${encodeUniqueId(workTaskId)}`
        history.push(url)
    }

    if (!displayData.length) {
        return null
    }

    return (
        <LatestActivitiesWidgetCard
            title={translateText(336)}
            headerAction={renderWidgetHeaderAction()}
            icon="menu"
            widgetVariant="startpage"
            iconProps={{ size: "small" }}
        >
            <Stack gap={1} overflow="hidden">
                {props.state.loading && <Loader size="small" />}
                <LatestActivitiesTable
                    columns={getColumns()}
                    rows={displayData}
                    rowCap={4}
                    overflowY="auto"
                    headerStyle="hidden"
                    headerBackground="transparent"
                    onRowClick={(row) => handleOnRowClick(row.id)}
                    rowStyles={{ cursor: "pointer" }}
                />
            </Stack>
        </LatestActivitiesWidgetCard>
    )
}

export default connectComponent(Actions, RecentList)
